import { Keys } from '@progress/kendo-react-common';
import React, { ReactPortal } from 'react';
import ReactDOM from 'react-dom';
import {
  Action,
  ActionType,
  Dialog,
  Dispatcher,
  DockType,
  Position,
  WidgetStage
} from '../../../index';
import {
  Window as KendoWindow,
  WindowActionsEvent
} from '@progress/kendo-react-dialogs';
import _ from 'lodash';
import Connect from '../../../utility/window/Connect';
import DispatcherWindowManager from '../../../utility/window/WindowManager';
import { WidgetProps, WidgetState } from './Types';

const FADE = {
  in: 0.5, // half second
  out: 0.5
};

class Widget extends Dialog {
  /**
   * FadeOrder ensures that every unique download is starting widget only ones.
   */
  private fadeOrder: { downloadId: string, done: boolean }[] = [];
  private widgetIsEscaped: boolean = false;
  protected multiWindow = false;

  state: WidgetState = {
    visible: true,
    resizable: true,
    draggable: true,
    width: 400,
    height: 400,
    minWidth: 400,
    minHeight: 400,
    title: '',
    stage: WidgetStage.default,
    style: {}
  };

  constructor(props: WidgetProps) {
    super(props);
    this.baseClassPrefix = 'tp-window-';

    const { placeholders = {}, skipTranslation = false } = props;
    let title = props.title;
    title = skipTranslation && title ? title : this.translate(title, placeholders);
    const offset = 20;
    this.state = {
      ...this.state,
      ...props,
      originalTitle: title,
      style: this.getFadeStyle(1, 'in'),
      title,
      initialTop: window.innerHeight - (props.height || props.minHeight) - offset,
      initialLeft: window.innerWidth - (props.width || props.minWidth) - offset
    };
    this.moduleId = props.id;
    const actionName: string = this.getCloseActionName();
    _.merge(Dispatcher.appActions, {
      [actionName]: this.getCloseWidgetAction()
    });
  }

  dispatch(actionName: string, actionType?: ActionType | Position, data?: any, methodName?: string): void {
    if (_.isUndefined(data)) {
      data = {};
    }
    _.merge(data, {
      moduleId: this.moduleId
    });

    if (!actionType) {
      actionType = {};
    }

    if (_.isString(actionType)) {
      actionType = {
        pos: actionType,
        persistent: ['moduleId']
      };
    } else {
      if (Array.isArray(actionType.persistent)) {
        actionType.persistent.push('moduleId');
      } else {
        actionType.persistent = ['moduleId'];
      }
    }

    if (this.props.dispatcherId) {
      // If widget is created via dispatcher which is not mainDispatcher, execute his actions trough that dispatcher.
      actionType.dispatcherId = this.props.dispatcherId;
    }
    if (actionName) {
      super.dispatch(actionName, actionType, data, methodName);
    }
  }

  moduleDidMount = () => {
    super.moduleDidMount();
    DispatcherWindowManager.openWindow(this.moduleName, {
      previousFocus: this.props.previousFocus
    });
  };

  /**
   * @inheritDoc
   */
  getCloseActionName(): string {
    return `${super.getCloseActionName()}_${this.moduleId}`;
  }

  /**
   * @inheritDoc
   */
  public getCloseWidgetAction(): Action {
    return [{
      moduleId: this.moduleId,
      moduleName: this.moduleName,
      name: 'close'
    }];
  }

  /**
   * Closes Widget with passed moduleId.
   * @param moduleId
   */
  public close = (moduleId: string): void => {
    if (this.moduleId === moduleId) {
      let visible = false;
      this.setState({ visible });
      this.unmountWidget();
    }
  };

  /**
   * Method with which you can update title.
   * @param title
   * @param placeholders
   */
  updateTitle(title: string, placeholders: { [key: string]: string }) {
    const { skipTranslation, placeholders: originalPlaceholders } = this.props;
    this.setState({
      title: skipTranslation ? title : this.translate(title, _.merge(originalPlaceholders, placeholders))
    });
  }

  getTitleWithLoader() {
    let { title = null, skipTranslation = false } = this.props;
    if (title && !skipTranslation) {
      title = this.translate(title, this.props.placeholders);
    }
    return (
      <>
        {title ? title : this.state.title} {this.renderMask()}
      </>
    );
  }

  /**
   * Handles widget modes state. FULLSCREEN, MINIMIZED or DEFAULT.
   * @param e
   */
  public handleStageChange(e: WindowActionsEvent) {
    const stage = e.state;
    this.setState({
      stage,
      title: stage === WidgetStage.minimized ? this.getTitleWithLoader() : this.state.originalTitle
    });
  }

  render(): any {
    const accessibility = this.props.accessibility;
    const accessibilityContent = accessibility ? {
      ['aria-live']: accessibility.ariaLive,
      role: accessibility.role
    } : {};
    let windowStageCls: string = `${this.getDialogAdditionalClasses()}`;
    switch (this.state.stage) {
      case WidgetStage.minimized:
        windowStageCls += ` ${this.baseClassPrefix}minimized`;
        break;
      case WidgetStage.fullScreen:
        windowStageCls += ` ${this.baseClassPrefix}maximized`;
        break;
      default:
        break;
    }
    return (
      <div
        {...accessibilityContent}
        className={windowStageCls}
      >
        {this.renderBody()}
      </div>
    );
  }

  getChildren = () => [<div
    // <debug>
    {...{ 'data-cy': `cy-${this.moduleName}-content` }}
    // </debug>
    className={`${this.baseClassPrefix}inner-content`}
  >
    {this.renderChildren()}
    {this.renderMask()}
  </div>, this.renderToolbar()];

  /**
   * Closes widget by executing dispatcher action.
   */
  doCloseWidget() {
    const closeAction = this.closeAction || this.getCloseActionName();
    this.dispatch(closeAction, Position.end);

    if (this.escapeActionName) {
      this.dispatch(this.escapeActionName, Position.end);
    }
  }

  /**
   * Closing widget by executing dispatcher action.
   * @param e
   */
  closeWidget = (e: WindowActionsEvent) => {
    e.nativeEvent.preventDefault();
    if (this.widgetIsEscaped) {
      // kendo react fires onClose event twice when escape is pressed on keyboard
      // so we prevent execution of closingWidget on already unmounted module
      return;
    }
    this.widgetIsEscaped = e.nativeEvent.keyCode === Keys.esc;

    this.doCloseWidget();
  };

  /**
   * Renders title or title with buttons if dock set to Dock.top.
   */
  getTitle() {
    let { title } = this.state;
    let topToolbar = this.renderToolbar(DockType.top);
    if (topToolbar) {
      title = topToolbar;
    }
    return title;
  }

  /**
   * Creates widget action button.
   * @param props
   * @param type
   */
  protected getWidgetActionButton = (props: any, type: 'minimize' | 'maximize' | 'restore') => (
    <button
      aria-label={this.translate(`Actions.${type}`)}
      className='k-button-icon k-button k-flat'
      onClick={props.onClick}
      // @ts-ignore
      stage={props.stage}
    >
      <span
        className={`k-icon k-i-window-${type}`}
        role='presentation'
      />
    </button>
  );
  /**
   * Getter for minimize widget action button.
   * @param props
   */
  private getMinimizeButton = (props: any) => this.getWidgetActionButton(props, 'minimize');
  /**
   * Getter for maximize widget action button.
   * @param props
   */
  private getMaximizeButton = (props: any) => this.getWidgetActionButton(props, 'maximize');
  /**
   * Getter for restore widget action button.
   * @param props
   */
  private getRestoreButton = (props: any) => this.getWidgetActionButton(props, 'restore');
  /**
   * Set fade-in style.
   */
  private fadeIn = () => this.setState({
    style: this.getFadeStyle(FADE.in, 'in')
  });
  /**
   * Set fade-out style.
   */
  private fadeOut = () => this.setState({
    style: this.getFadeStyle(FADE.out, 'out')
  });
  /**
   * Executed via dispatcher operation. Sets fade-in style for every distinct download.
   * @param downloadId
   */
  private widgetFadeIn = (downloadId: string) => {
    let match = _.find(this.fadeOrder, {
      downloadId
    });
    if (!match) {
      this.fadeOrder.push({
        downloadId,
        done: false
      });
      this.fadeIn();
    }
  };
  /**
   * Executed via dispatcher operation. Sets fade-out style for every distinct download.
   * @param downloadId
   */
  private widgetFadeOut = (downloadId: string) => {
    let match = _.find(this.fadeOrder, downloadId ? {
      downloadId
    } : {
      done: false
    });
    if (match && !match.done) {
      match.done = true;
    }
    this.fadeOut();
  };
  /**
   * Getter for fade style.
   * @param numberOfSeconds
   * @param mode
   */
  private getFadeStyle = (numberOfSeconds: number, mode: 'in' | 'out') => ({
    opacity: mode === 'in' ? 1 : 0,
    transition: `opacity ${numberOfSeconds}s ease`
  });

  renderBody(): JSX.Element | ReactPortal | null {
    if (this.state.visible) {
      return (
        <KendoWindow
          {...this.state}
          title={this.getTitle()}
          onClose={(e: WindowActionsEvent) => this.closeWidget(e)}
          onStageChange={(e: WindowActionsEvent) => this.handleStageChange(e)}
          children={this.childrenWithKeys()}
          minimizeButton={this.getMinimizeButton}
          maximizeButton={this.getMaximizeButton}
          restoreButton={this.getRestoreButton}
          closeButton={props => (
            <button
              aria-label={this.translate('Actions.close')}
              className="k-button-icon k-button k-flat"
              onClick={props.onClick}
              // @ts-ignore
              stage={props.stage}
            >
              <span
                className="k-icon k-i-close"
                role="presentation"
              />
            </button>
          )}
        />);
    }

    return null;
  }

  /**
   * Sets property 'dialogNodeElements' with focusable nodes of dialog
   * @param {HTMLElement} domNodeBody Dom element of active dialog
   */
  protected setActiveDialogNodeElements = (domNodeBody: HTMLElement = null) => {
    // Do nothing for widgets and download widgets
  };

  /**
   * Unmount widget component and remove wrapping div from <div id="widgets">...</div>
   */
  unmountWidget() {
    const { id } = this.state;
    ReactDOM.unmountComponentAtNode((this.getDomNode() as any).parentNode);
    Connect.remove(id);
  }
}

export default Widget;
